import type { ApexOptions } from 'apexcharts'
import type { ChartSeriesItem, ValueFormatter } from '../types/chart.types'
import { ChartStyleColors } from './chart-colors'
import { compactNumberFormatter, shortMonthYearFormatter, localeNumberFormatter } from './chart-formatters'
import { createStandardTooltip, createTooltipWithFooter } from './chart-tooltip'

export interface BarChartConfig {
  series: ChartSeriesItem[]
  categories: string[]
  height?: number
  stacked?: boolean
  columnWidth?: string
  yAxisFormatter?: ValueFormatter
  tooltipValueFormatter?: ValueFormatter
  xAxisLabelFormatter?: (value: string) => string
}

export interface StackedBarWithLineConfig extends BarChartConfig {
  lineSeries: ChartSeriesItem
}

export interface BarChartWithFooterConfig extends BarChartConfig {
  footerLabel: string
  footerValues: number[]
  footerColorGetter?: (value: number) => string
}

export const getBaseBarChartOptions = (): ApexOptions => ({
  chart: {
    type: 'bar',
    height: 350,
    toolbar: {
      show: true,
      tools: {
        download: false,
        selection: false,
        zoom: false,
        zoomin: false,
        zoomout: false,
        pan: false,
        reset: false
      }
    },
    fontFamily: 'inherit',
    animations: {
      enabled: true,
      speed: 300,
      dynamicAnimation: { enabled: true, speed: 300 }
    }
  },
  dataLabels: { enabled: false },
  legend: { show: false },
  plotOptions: {
    bar: {
      horizontal: false,
      columnWidth: '60%',
      borderRadius: 4,
      borderRadiusApplication: 'end'
    }
  },
  xaxis: {
    labels: {
      style: { fontSize: '12px', colors: ChartStyleColors.labelColor }
    },
    crosshairs: {
      fill: { type: 'solid', color: ChartStyleColors.crosshairColor }
    }
  },
  yaxis: {
    labels: {
      style: { fontSize: '12px', colors: ChartStyleColors.labelColor }
    }
  },
  grid: {
    borderColor: ChartStyleColors.gridColorDark,
    strokeDashArray: 4,
    xaxis: { lines: { show: false } },
    yaxis: { lines: { show: true } }
  },
  tooltip: {
    shared: true,
    intersect: false,
    followCursor: false
  },
  states: {
    hover: { filter: { type: 'none' } },
    active: { filter: { type: 'none' } }
  }
})

export const buildBarChartOptions = (config: BarChartConfig): ApexOptions => {
  const {
    series,
    categories,
    height = 350,
    stacked = false,
    columnWidth = '60%',
    yAxisFormatter = compactNumberFormatter,
    tooltipValueFormatter = localeNumberFormatter,
    xAxisLabelFormatter = shortMonthYearFormatter
  } = config

  const baseOptions = getBaseBarChartOptions()

  const apexSeries = series.map((s) => ({
    name: s.name,
    data: s.data,
    color: s.color,
    type: 'bar' as const
  }))

  return {
    ...baseOptions,
    chart: {
      ...baseOptions.chart,
      height,
      stacked
    },
    series: apexSeries,
    plotOptions: {
      bar: {
        ...(baseOptions.plotOptions?.bar || {}),
        columnWidth
      }
    },
    xaxis: {
      ...baseOptions.xaxis,
      categories,
      labels: {
        ...(baseOptions.xaxis as ApexXAxis).labels,
        formatter: xAxisLabelFormatter
      }
    },
    yaxis: {
      ...baseOptions.yaxis,
      labels: {
        ...(baseOptions.yaxis as ApexYAxis).labels,
        formatter: yAxisFormatter
      }
    },
    tooltip: {
      ...baseOptions.tooltip,
      custom: createStandardTooltip({
        valueFormatter: (value) => tooltipValueFormatter(value, 0)
      })
    }
  }
}

export const buildStackedBarChartOptions = (config: BarChartConfig): ApexOptions => {
  const options = buildBarChartOptions({
    ...config,
    stacked: true,
    columnWidth: config.columnWidth || '70%'
  })

  return {
    ...options,
    plotOptions: {
      bar: {
        ...(options.plotOptions?.bar || {}),
        borderRadiusApplication: 'end',
        borderRadiusWhenStacked: 'last'
      }
    }
  }
}

export const buildStackedBarWithLineOptions = (config: StackedBarWithLineConfig): ApexOptions => {
  const {
    series,
    lineSeries,
    categories,
    height = 400,
    columnWidth = '70%',
    yAxisFormatter = compactNumberFormatter,
    tooltipValueFormatter = localeNumberFormatter,
    xAxisLabelFormatter = shortMonthYearFormatter
  } = config

  const baseOptions = getBaseBarChartOptions()

  const barApexSeries = series.map((s) => ({
    name: s.name,
    data: s.data,
    color: s.color,
    type: 'bar' as const
  }))

  const lineApexSeries = {
    name: lineSeries.name,
    data: lineSeries.data,
    color: lineSeries.color,
    type: 'line' as const
  }

  const allSeries = [...barApexSeries, lineApexSeries]

  const strokeWidths = [...series.map(() => 0), 2]
  const markerSizes = [...series.map(() => 0), 3]
  const colors = [...series.map((s) => s.color), lineSeries.color]

  return {
    ...baseOptions,
    chart: {
      ...baseOptions.chart,
      height,
      stacked: true
    },
    series: allSeries,
    plotOptions: {
      bar: {
        ...(baseOptions.plotOptions?.bar || {}),
        columnWidth,
        borderRadiusApplication: 'end',
        borderRadiusWhenStacked: 'last'
      }
    },
    stroke: {
      width: strokeWidths,
      curve: 'straight'
    },
    markers: {
      size: markerSizes,
      colors,
      strokeColors: lineSeries.color,
      strokeWidth: 0,
      hover: { size: 7 }
    },
    xaxis: {
      ...baseOptions.xaxis,
      categories,
      labels: {
        ...(baseOptions.xaxis as ApexXAxis).labels,
        formatter: xAxisLabelFormatter
      }
    },
    yaxis: {
      ...baseOptions.yaxis,
      labels: {
        ...(baseOptions.yaxis as ApexYAxis).labels,
        formatter: yAxisFormatter
      }
    },
    tooltip: {
      ...baseOptions.tooltip,
      custom: createStandardTooltip({
        valueFormatter: (value) => tooltipValueFormatter(value, 0)
      })
    }
  }
}

export const buildStackedBarWithFooterOptions = (config: BarChartWithFooterConfig): ApexOptions => {
  const {
    series,
    categories,
    height = 400,
    columnWidth = '70%',
    yAxisFormatter = compactNumberFormatter,
    tooltipValueFormatter = localeNumberFormatter,
    xAxisLabelFormatter = shortMonthYearFormatter,
    footerLabel,
    footerValues,
    footerColorGetter
  } = config

  const baseOptions = buildStackedBarChartOptions({
    series,
    categories,
    height,
    columnWidth,
    yAxisFormatter,
    xAxisLabelFormatter
  })

  return {
    ...baseOptions,
    stroke: {
      width: 0,
      curve: 'straight'
    },
    tooltip: {
      ...baseOptions.tooltip,
      custom: createTooltipWithFooter({
        valueFormatter: (value) => tooltipValueFormatter(value, 2),
        footerLabel,
        footerValueGetter: (dataPointIndex) => footerValues[dataPointIndex],
        footerColorGetter
      })
    }
  }
}
